import React from 'react';
import { Box, Paper, Skeleton, Tooltip } from '@mui/material';
import {
  Payments as PaymentsIcon,
  Inventory2 as InventoryIcon,
  LocalPharmacy as PharmacyIcon,
  LockClock as LockIcon,
  EditCalendar as EditIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { KpiData, PeriodStatus } from '../types';
import { fmtSum } from '../utils';

interface KpiBarProps {
  kpi: KpiData | null;
  kpiLoading: boolean;
  periodStatus: PeriodStatus | null;
}

const KpiBar: React.FC<KpiBarProps> = ({ kpi, kpiLoading, periodStatus }) => {
  const theme = useTheme();

  const isInputPeriod = periodStatus?.canCreate ?? true;

  type SegmentColor = 'success' | 'info' | 'primary' | 'error' | 'warning';

  const segments: {
    label: string;
    value: React.ReactNode;
    color: SegmentColor;
    textColor: string;
    icon: React.ReactNode;
    width: string;
    tooltip?: string;
  }[] = [
    {
      label: 'Сумма',
      value: kpi ? fmtSum(kpi.totalSum) : '—',
      color: 'success',
      textColor: '#16a34a',
      icon: <PaymentsIcon />,
      width: '26%',
    },
    {
      label: 'Количество',
      value: kpi
        ? (kpi.totalQtyPcs ?? 0) > 0
          ? `${kpi.totalQty.toLocaleString('ru-RU')} уп. / ${kpi.totalQtyPcs.toLocaleString('ru-RU')} шт.`
          : `${kpi.totalQty.toLocaleString('ru-RU')} уп.`
        : '—',
      color: 'info',
      textColor: '#0284c7',
      icon: <InventoryIcon />,
      width: '42%',
    },
    {
      label: 'Позиций',
      value: kpi?.count ?? '—',
      color: 'primary',
      textColor: '#2563eb',
      icon: <PharmacyIcon />,
      width: '18%',
    },
    {
      label: 'Режим',
      value: isInputPeriod ? 'Ввод' : 'Блокировка',
      color: isInputPeriod ? 'success' : 'error',
      textColor: isInputPeriod ? '#16a34a' : '#dc2626',
      icon: isInputPeriod ? <EditIcon /> : <LockIcon />,
      width: '14%',
      tooltip: periodStatus?.periodName,
    },
  ];

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{ display: 'flex', height: 44, borderRadius: 2, overflow: 'hidden', bgcolor: 'white', width: '100%' }}
    >
      {segments.map((seg, i) => (
        <Tooltip key={seg.label} title={seg.tooltip ?? ''} placement="bottom" disableHoverListener={!seg.tooltip}>
          <Box
            sx={{
              flex: `0 0 ${seg.width}`,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.75,
              overflow: 'hidden',
              borderRight: i < segments.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
              minWidth: 0,
            }}
          >
            {/* Watermark */}
            <Box sx={{
              position: 'absolute', right: -6, bottom: -8,
              color: theme.palette[seg.color].main,
              opacity: 0.08, transform: 'rotate(-15deg)',
              zIndex: 0, pointerEvents: 'none',
              '& svg': { fontSize: '44px' },
            }}>
              {seg.icon}
            </Box>

            {/* Label */}
            <Box component="span" sx={{
              fontSize: 11, fontWeight: 700, color: 'text.secondary',
              textTransform: 'uppercase', letterSpacing: 0.3,
              zIndex: 1, whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              {seg.label}
            </Box>

            {/* Value */}
            {kpiLoading && seg.label !== 'Режим'
              ? <Skeleton width={50} height={16} sx={{ zIndex: 1 }} />
              : (
                <Box component="span" sx={{
                  fontSize: 14, fontWeight: 800, color: seg.textColor,
                  zIndex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {seg.value}
                </Box>
              )}
          </Box>
        </Tooltip>
      ))}
    </Paper>
  );
};

export default KpiBar;
